import { useEffect, useRef, useState } from 'react';

import { Box, Divider, Paper, Skeleton } from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  getWalletById,
  getWalletSummaryInfo,
  updateWalletName,
} from 'src/services/wallet';
import { setBannerMessage } from 'src/store/display';
import { truncateText } from 'src/utils/string';

import { useMonitorContext } from '../../MonitorContextProvider';
import { GuestEnvironmentAlert } from '../../components/GuestEnvironmentAlert';
import { GuestEnvironmentSelector } from '../../components/Guests/GuestEnvironmentSelector';
import { EditWalletFunds } from '../components/EditWalletFunds';
import EditWalletName from '../components/EditWalletName';

export function WalletEdit() {
  const { walletId, ownerId } = useParams<{
    walletId: string;
    ownerId?: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    setBreadcrumbsItens,
    wallet,
    setWallet,
    setGlobalFundsCount,
    setGuestEnvironmentOwnerId,
  } = useMonitorContext();
  const [isEditing, setIsEditing] = useState(false);
  const [walletName, setWalletName] = useState('');
  const intentionalSaveRef = useRef(false);

  const isGuestContext = !!ownerId;

  useQuery({
    queryKey: ['wallets-summary-info-edit'],
    queryFn: () => getWalletSummaryInfo(),
    onSuccess: (data) => {
      setGlobalFundsCount(data?.fundsCount || 0);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { isLoading: isLoadingWallet, refetch: refetchWallet } = useQuery({
    queryKey: ['wallets', walletId],
    queryFn: () => getWalletById(walletId),
    onSuccess: (data) => {
      setWallet(data);
      setWalletName(data?.name || '');
    },
    enabled: !!walletId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { mutate: updateWalletNameMutation } = useMutation({
    mutationFn: (name: string) => updateWalletName(walletId, name),
    onSuccess: () => {
      refetchWallet();
      dispatch(
        setBannerMessage({
          message: 'Nome da carteira atualizado com sucesso',
          type: 'success',
          autoclose: true,
        }),
      );
    },
    onError: () => {
      dispatch(
        setBannerMessage({
          message: 'Ocorreu um erro ao atualizar o nome da carteira',
          type: 'error',
          autoclose: true,
        }),
      );
    },
  });

  const handleTextClick = () => {
    setIsEditing(true);
    intentionalSaveRef.current = false;
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWalletName(event.target.value);
  };

  const saveIfChanged = () => {
    if (walletName.trim() !== wallet?.name?.trim()) {
      updateWalletNameMutation(walletName.trim());
    }
  };

  const handleSave = () => {
    intentionalSaveRef.current = true;
    setIsEditing(false);
    saveIfChanged();
  };

  const handleCancel = () => {
    intentionalSaveRef.current = false;
    setIsEditing(false);
    setWalletName(wallet?.name || '');
  };

  const handleBlur = () => {
    if (!intentionalSaveRef.current) {
      setIsEditing(false);
      setWalletName(wallet?.name || '');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  const handleWalletUpdate = () => {
    refetchWallet();
  };

  useEffect(() => {
    if (isGuestContext && ownerId) {
      setGuestEnvironmentOwnerId(ownerId);
      return () => {
        setGuestEnvironmentOwnerId(null);
      };
    }
  }, [isGuestContext, ownerId, setGuestEnvironmentOwnerId]);

  useEffect(() => {
    if (wallet) {
      const walletLink = ownerId
        ? `/monitor/${ownerId}/wallet/${walletId}`
        : `/monitor/wallet/${walletId}`;

      setBreadcrumbsItens((prev) => [
        ...prev,
        {
          title: `Carteira - ${truncateText(wallet?.name, 25)}`,
          link: walletLink,
        },
        'Editar Carteira',
      ]);

      return () => {
        setBreadcrumbsItens((prev) => prev.slice(0, -2));
      };
    }

    if (!walletId) {
      const monitorLink = ownerId ? `/monitor/${ownerId}` : '/monitor';
      history.push(monitorLink);
    }

    if (walletId && !wallet) {
      refetchWallet();
    }
  }, [walletId, wallet, history, refetchWallet, ownerId]);

  return (
    <>
      {isGuestContext && (
        <>
          <GuestEnvironmentSelector />
          <GuestEnvironmentAlert />
        </>
      )}
      {isLoadingWallet ? (
        <Paper component="section" elevation={2} sx={{ p: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Divider sx={{ my: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={40} />
        </Paper>
      ) : (
        <Paper component="section" elevation={2} sx={{ p: 2 }}>
          <EditWalletName
            isEditing={isEditing}
            walletName={walletName}
            handleTextChange={handleTextChange}
            handleBlur={handleBlur}
            handleKeyPress={handleKeyPress}
            handleSave={handleSave}
            handleCancel={handleCancel}
            handleTextClick={handleTextClick}
          />
          <Divider sx={{ my: 1 }} />

          <Box sx={{ width: '100%', mt: 2 }}>
            {wallet && (
              <EditWalletFunds wallet={wallet} onSuccess={handleWalletUpdate} />
            )}
          </Box>
        </Paper>
      )}
    </>
  );
}
